/**
 * Keyboard layout detection and default mappings.
 *
 * Mappings use physical key codes (KeyQ, KeyW, etc.) so the same physical
 * keyboard positions are used across QWERTY, QWERTZ, and AZERTY layouts.
 */

interface KeyboardLayoutNavigator extends Navigator {
  keyboard?: {
    getLayoutMap: () => Promise<Map<string, string>>
  }
}

let cachedLayoutMap: Map<string, string> | null = null

export async function getKeyboardLayoutMap(): Promise<Map<string, string>> {
  if (cachedLayoutMap) return cachedLayoutMap

  try {
    const keyboard = (navigator as KeyboardLayoutNavigator).keyboard
    if (keyboard?.getLayoutMap) {
      cachedLayoutMap = await keyboard.getLayoutMap()
      return cachedLayoutMap
    }
  } catch (error) {
    console.warn('Keyboard API not available, using fallback labels', error)
  }

  return new Map()
}

export function getLayoutAwareKeyLabelSync(code: string, layoutMap?: Map<string, string>): string {
  const char = layoutMap?.get(code) ?? cachedLayoutMap?.get(code)
  return char ? char.toUpperCase() : getFallbackKeyLabel(code)
}

export function isReservedKey(code: string): boolean {
  return code === 'Space'
}


export async function initKeyboardLayout(): Promise<void> {
  await getKeyboardLayoutMap()
}

function getFallbackKeyLabel(code: string): string {
  const specialKeys: Record<string, string> = {
    ControlLeft: 'LCtrl',
    ControlRight: 'RCtrl',
    ShiftLeft: 'LShift',
    ShiftRight: 'RShift',
    AltLeft: 'LAlt',
    AltRight: 'RAlt',
    MetaLeft: 'LMeta',
    MetaRight: 'RMeta',
    Enter: 'Enter',
    Backspace: 'Back',
    Tab: 'Tab',
    Escape: 'Esc',
    Delete: 'Del',
    Insert: 'Ins',
    Home: 'Home',
    End: 'End',
    PageUp: 'PgUp',
    PageDown: 'PgDn',
    ArrowUp: 'Up',
    ArrowDown: 'Down',
    ArrowLeft: 'Left',
    ArrowRight: 'Right',
    F1: 'F1',
    F2: 'F2',
    F3: 'F3',
    F4: 'F4',
    F5: 'F5',
    F6: 'F6',
    F7: 'F7',
    F8: 'F8',
    F9: 'F9',
    F10: 'F10',
    F11: 'F11',
    F12: 'F12',
    Numpad0: 'N0',
    Numpad1: 'N1',
    Numpad2: 'N2',
    Numpad3: 'N3',
    Numpad4: 'N4',
    Numpad5: 'N5',
    Numpad6: 'N6',
    Numpad7: 'N7',
    Numpad8: 'N8',
    Numpad9: 'N9',
    NumpadAdd: 'N+',
    NumpadSubtract: 'N-',
    NumpadMultiply: 'N*',
    NumpadDivide: 'N/',
    NumpadEnter: 'NEnter',
    NumpadDecimal: 'N.',
    CapsLock: 'Caps',
    NumLock: 'NumLk',
    ScrollLock: 'ScrLk',
    Pause: 'Pause',
    PrintScreen: 'PrtSc',
    ContextMenu: 'Menu',
    Minus: '-',
    Equal: '=',
    BracketLeft: '[',
    BracketRight: ']',
    Backslash: '\\',
    Semicolon: ';',
    Quote: "'",
    Backquote: '`',
    Comma: ',',
    Period: '.',
    Slash: '/',
    IntlBackslash: '<',
  }

  if (specialKeys[code]) return specialKeys[code]
  if (code.startsWith('Key')) return code.slice(3)
  if (code.startsWith('Digit')) return code.slice(5)

  return code.length > 5 ? code.slice(0, 5) : code
}
